import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { assertAdmin } from "@/lib/admin-guard";
import { z } from "zod";

/** Generic / filename-ish strings that don't count as descriptive alt text. */
const GENERIC_ALT = /^(image|photo|picture|img|untitled|gallery|workshop)[\s_-]*\d*$/i;
const FILENAME_LIKE = /\.(jpe?g|png|webp|avif|gif|bmp|tiff?)\s*$/i;

/**
 * Shared alt-text validator. Enforces a descriptive, screen-reader-friendly
 * alt: ≥10 chars, ≥2 words, not a filename, not a generic placeholder.
 */
export function validateAltText(raw: string): { ok: true } | { ok: false; reason: string } {
  const v = (raw || "").trim();
  if (v.length < 10) return { ok: false, reason: "Alt text must be at least 10 characters." };
  if (v.length > 300) return { ok: false, reason: "Alt text must be 300 characters or fewer." };
  if (FILENAME_LIKE.test(v)) return { ok: false, reason: "Alt text looks like a filename — describe what's in the image instead." };
  if (GENERIC_ALT.test(v)) return { ok: false, reason: "Alt text is too generic — describe the subject, action, or context." };
  const words = v.split(/\s+/).filter(Boolean);
  if (words.length < 2) return { ok: false, reason: "Alt text must be at least two words." };
  return { ok: true };
}

export const descriptiveAltSchema = z
  .string()
  .min(10, "Alt text must be at least 10 characters.")
  .max(300, "Alt text must be 300 characters or fewer.")
  .refine((s) => validateAltText(s).ok, (s) => {
    const r = validateAltText(s);
    return { message: r.ok ? "" : r.reason };
  });

// Lazy admin client — avoids importing service-role client at module scope.
async function getAdmin() {
  const mod = await import("@/integrations/supabase/client.server");
  return mod.supabaseAdmin;
}

// Lazy publishable client used for public reads so RLS applies.
async function getPublic() {
  const url = process.env.SUPABASE_URL;
  const key = process.env.SUPABASE_PUBLISHABLE_KEY || process.env.SUPABASE_ANON_KEY;
  if (!url || !key) {
    // Fall back to admin client on misconfigured environments rather than crashing.
    return await getAdmin();
  }
  const { createClient } = await import("@supabase/supabase-js");
  return createClient(url, key, { auth: { persistSession: false, autoRefreshToken: false } });
}

export interface GalleryVariant {
  url: string;
  path: string;
  width: number;
  height: number;
}

export interface GalleryVariants {
  thumb?: GalleryVariant;
  medium?: GalleryVariant;
}

export interface GalleryPhoto {
  id: string;
  storage_path: string;
  public_url: string;
  caption: string | null;
  alt_text: string;
  category: string;
  sort_order: number;
  featured: boolean;
  is_active: boolean;
  width: number | null;
  height: number | null;
  variants: GalleryVariants | null;
  created_at: string;
}

/** Public — list active photos, optionally limit to featured for the homepage strip. */
export const listPublicGalleryPhotos = createServerFn({ method: "GET" })
  .validator((d: { featuredOnly?: boolean; limit?: number } | undefined) => d || {})
  .handler(async ({ data }) => {
    const supabaseAdmin = await getPublic();
    let q = supabaseAdmin
      .from("gallery_photos")
      .select("*")
      .eq("is_active", true)
      .order("sort_order", { ascending: false })
      .order("created_at", { ascending: false });
    if (data.featuredOnly) q = q.eq("featured", true);
    if (data.limit) q = q.limit(data.limit);
    const { data: rows, error } = await q;
    if (error) {
      console.error("listPublicGalleryPhotos failed", error);
      return { photos: [] as GalleryPhoto[] };
    }
    return { photos: (rows || []) as unknown as GalleryPhoto[] };
  });

/** Admin — list all photos including inactive. */
export const listAdminGalleryPhotos = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator(z.object({ password: z.string().min(1) }))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const supabaseAdmin = await getAdmin();
    const { data: rows, error } = await supabaseAdmin
      .from("gallery_photos")
      .select("*")
      .order("sort_order", { ascending: false })
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return { photos: (rows || []) as unknown as GalleryPhoto[] };
  });

const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp", "image/avif"];

const variantSchema = z.object({
  base64: z.string().min(10),
  contentType: z.string().min(1),
  filename: z.string().min(1).max(200),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
});

function decodeB64(b64: string): Uint8Array {
  const clean = b64.replace(/^data:[^;]+;base64,/, "");
  return Uint8Array.from(atob(clean), (c) => c.charCodeAt(0));
}

async function uploadOne(bytes: Uint8Array, contentType: string, key: string) {
  const supabaseAdmin = await getAdmin();
  const { error } = await supabaseAdmin.storage
    .from("gallery")
    .upload(key, bytes, { contentType, upsert: false });
  if (error) throw new Error(`Upload failed: ${error.message}`);
  const { data: pub } = supabaseAdmin.storage.from("gallery").getPublicUrl(key);
  return pub.publicUrl;
}

function makeKey(filename: string, suffix = "") {
  const ext = filename.split(".").pop()?.toLowerCase() || "jpg";
  const safeBase = filename
    .replace(/\.[^.]+$/, "")
    .replace(/[^a-zA-Z0-9-_]/g, "-")
    .slice(0, 60);
  const stamp = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  return `${stamp}-${safeBase}${suffix}.${ext}`;
}

/** Admin — upload a photo (base64 from the browser) plus optional thumb/medium variants. */
export const uploadGalleryPhoto = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator(
    z.object({
      password: z.string().min(1),
      filename: z.string().min(1).max(200),
      contentType: z.string().min(1),
      base64: z.string().min(10),
      alt_text: descriptiveAltSchema,
      caption: z.string().max(500).optional(),
      category: z.string().min(1).max(60).default("Workshop"),
      featured: z.boolean().default(false),
      width: z.number().int().positive().optional(),
      height: z.number().int().positive().optional(),
      thumb: variantSchema.optional(),
      medium: variantSchema.optional(),
    })
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const supabaseAdmin = await getAdmin();
    if (!ALLOWED_TYPES.includes(data.contentType)) {
      throw new Error("Only JPG, PNG, WebP or AVIF images are allowed");
    }
    const bytes = decodeB64(data.base64);
    if (bytes.byteLength > 10 * 1024 * 1024) throw new Error("Image is too large (max 10 MB)");

    const fullKey = makeKey(data.filename);
    const fullUrl = await uploadOne(bytes, data.contentType, fullKey);

    const variants: GalleryVariants = {};
    const cleanup: string[] = [fullKey];
    try {
      if (data.thumb) {
        const k = makeKey(data.thumb.filename, "-thumb");
        const url = await uploadOne(decodeB64(data.thumb.base64), data.thumb.contentType, k);
        variants.thumb = { url, path: k, width: data.thumb.width, height: data.thumb.height };
        cleanup.push(k);
      }
      if (data.medium) {
        const k = makeKey(data.medium.filename, "-medium");
        const url = await uploadOne(decodeB64(data.medium.base64), data.medium.contentType, k);
        variants.medium = { url, path: k, width: data.medium.width, height: data.medium.height };
        cleanup.push(k);
      }

      const insertRow = {
        storage_path: fullKey,
        public_url: fullUrl,
        alt_text: data.alt_text,
        caption: data.caption || null,
        category: data.category,
        featured: data.featured,
        is_active: true,
        sort_order: 0,
        width: data.width || null,
        height: data.height || null,
        variants: (Object.keys(variants).length ? variants : null) as never,
      };
      const { data: row, error: insErr } = await supabaseAdmin
        .from("gallery_photos")
        .insert(insertRow)
        .select()
        .single();
      if (insErr) throw new Error(insErr.message);
      return { photo: row as unknown as GalleryPhoto };
    } catch (err) {
      await supabaseAdmin.storage.from("gallery").remove(cleanup).catch(() => {});
      throw err;
    }
  });

export const updateGalleryPhoto = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator(
    z.object({
      password: z.string().min(1),
      id: z.string().uuid(),
      patch: z.object({
        alt_text: descriptiveAltSchema.optional(),
        caption: z.string().max(500).nullable().optional(),
        category: z.string().min(1).max(60).optional(),
        sort_order: z.number().int().optional(),
        featured: z.boolean().optional(),
        is_active: z.boolean().optional(),
      }),
    })
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const supabaseAdmin = await getAdmin();
    const { error } = await supabaseAdmin
      .from("gallery_photos")
      .update(data.patch)
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

/** Admin — bulk reorder. Pass an ordered list of ids; the first item gets the highest sort_order. */
export const reorderGalleryPhotos = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator(
    z.object({
      password: z.string().min(1),
      orderedIds: z.array(z.string().uuid()).min(1).max(500),
    })
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const supabaseAdmin = await getAdmin();
    const total = data.orderedIds.length;
    // Update sequentially — small batches, no PG transaction needed for sort_order tweaks.
    for (let i = 0; i < total; i++) {
      const sort_order = total - i; // first item = highest
      const { error } = await supabaseAdmin
        .from("gallery_photos")
        .update({ sort_order })
        .eq("id", data.orderedIds[i]);
      if (error) throw new Error(`Reorder failed at #${i}: ${error.message}`);
    }
    return { ok: true, updated: total };
  });

export const deleteGalleryPhoto = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator(z.object({ password: z.string().min(1), id: z.string().uuid() }))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const supabaseAdmin = await getAdmin();
    const { data: row } = await supabaseAdmin
      .from("gallery_photos")
      .select("storage_path,variants")
      .eq("id", data.id)
      .single();
    const paths: string[] = [];
    if (row?.storage_path) paths.push(row.storage_path);
    const v = (row?.variants ?? null) as GalleryVariants | null;
    if (v?.thumb?.path) paths.push(v.thumb.path);
    if (v?.medium?.path) paths.push(v.medium.path);
    if (paths.length) {
      await supabaseAdmin.storage.from("gallery").remove(paths).catch(() => {});
    }
    const { error } = await supabaseAdmin.from("gallery_photos").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

/** Admin — schema audit for /gallery JSON-LD. Validates ImageObject completeness from the DB. */
export const auditGallerySchema = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator(z.object({ password: z.string().min(1) }))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const supabaseAdmin = await getAdmin();
    const { data: rows, error } = await supabaseAdmin
      .from("gallery_photos")
      .select("*")
      .eq("is_active", true)
      .order("sort_order", { ascending: false })
      .order("created_at", { ascending: false })
      .limit(24);
    if (error) throw new Error(error.message);
    const photos = (rows || []) as unknown as GalleryPhoto[];

    const issues: { id: string; alt: string; problems: string[] }[] = [];
    for (const p of photos) {
      const probs: string[] = [];
      if (!p.public_url) probs.push("missing contentUrl");
      const altCheck = validateAltText(p.alt_text || "");
      if (!altCheck.ok) probs.push(altCheck.reason);
      if (!p.caption || p.caption.trim().length < 5) probs.push("missing description/caption");
      if (!p.width || !p.height) probs.push("missing width/height");
      if (!p.variants?.thumb || !p.variants?.medium) probs.push("missing thumbnail/medium variants");
      if (probs.length) issues.push({ id: p.id, alt: p.alt_text || "(untitled)", problems: probs });
    }

    return {
      total: photos.length,
      issues,
      ok: issues.length === 0 && photos.length > 0,
      summary:
        photos.length === 0
          ? "No active photos — gallery JSON-LD will be empty."
          : issues.length === 0
            ? `All ${photos.length} active photos have a complete ImageObject.`
            : `${issues.length} of ${photos.length} active photos are missing required fields.`,
    };
  });
